/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { keyframes } from "@emotion/react";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import type { ComponentProps } from "react";
import { useEffect, useState } from "react";

import type { ThemeType } from "../../../../global/emotion";
import { useTheme, zIndices } from "../../../../global/emotion";
import { useThemeValues } from "../../../hooks/useThemeValues";

function HoverCard({
  disabled = false,
  ...props
}: ComponentProps<typeof HoverCardPrimitive.Root> & {
  disabled?: boolean;
}) {
  const [open, setOpen] = useState<boolean>(props?.open ?? false);
  useEffect(() => {
    if (props?.open !== undefined) setOpen(props.open);
  }, [props?.open]);
  return (
    <HoverCardPrimitive.Root
      data-slot="hover-card"
      open={open}
      onOpenChange={disabled ? () => {} : setOpen}
      {...props}
    />
  );
}

function HoverCardTrigger({
  children,
  ...props
}: ComponentProps<typeof HoverCardPrimitive.Trigger>) {
  return (
    <HoverCardPrimitive.Trigger
      data-slot="hover-card-trigger"
      {...props}
      asChild
    >
      <div>{children}</div>
    </HoverCardPrimitive.Trigger>
  );
}

const bottomAnimation = keyframes({
  "0%": {
    opacity: 0,
    transform: "scale(95%) translateY(-2px)",
  },
  "100%": {
    opacity: 1,
    transform: "scale(100%) translate(0 0)",
  },
});

const leftAnimation = keyframes({
  "0%": {
    opacity: 0,
    transform: "scale(95%) translateX(-2px)",
  },
  "100%": {
    opacity: 1,
    transform: "scale(100%) translate(0 0)",
  },
});

const rightAnimation = keyframes({
  "0%": {
    opacity: 0,
    transform: "scale(95%) translateX(2px)",
  },
  "100%": {
    opacity: 1,
    transform: "scale(100%) translate(0 0)",
  },
});

const topAnimation = keyframes({
  "0%": {
    opacity: 0,
    transform: "scale(95%) translateY(2px)",
  },
  "100%": {
    opacity: 1,
    transform: "scale(100%) translate(0 0)",
  },
});

const popoverStyle: Interpolation<ThemeType> = (theme) => ({
  animationDirection: "normal",
  animationDuration: "150ms",
  animationFillMode: "forwards",
  animationIterationCount: 1,
  animationTimingFunction: "ease-in",
  background: theme.semanticColors.background,
  borderColor: theme.semanticColors.neutralsLow,
  borderRadius: theme.radius.m,
  borderStyle: "solid",
  borderWidth: theme.stroke.light,
  boxShadow: `${theme.dropShadow.none} ${theme.dropShadow.s} ${theme.dropShadow.ms} -1px ${theme.shades.shade10},
      ${theme.dropShadow.none} ${theme.dropShadow.xs} ${theme.dropShadow.s} -2px ${theme.shades.shade10}`,
  color: theme.semanticColors.primary,
  opacity: 0,
  outlineColor: theme.colors.transparent,
  outlineOffset: theme.stroke.regular,
  outlineStyle: "solid",
  outlineWidth: theme.stroke.regular,
  padding: theme.spacing.s,
  transformOrigin: "var(--radix-hover-card-content-transform-origin)",
  zIndex: theme.zIndices.modalContent,
  '&[data-state="closed"]': {
    animationDirection: "reverse",
  },
  '&[data-state="open"]': {
    '&[data-side="bottom"]': {
      animationName: bottomAnimation,
    },
    '&[data-side="left"]': {
      animationName: leftAnimation,
    },
    '&[data-side="right"]': {
      animationName: rightAnimation,
    },
    '&[data-side="top"]': {
      animationName: topAnimation,
    },
  },
});

function HoverCardContent({
  align = "center",
  sideOffset = 4,
  ...props
}: ComponentProps<typeof HoverCardPrimitive.Content>) {
  const { themeContainerRef } = useTheme();
  return (
    <HoverCardPrimitive.Portal
      data-slot="hover-card-portal"
      container={themeContainerRef.current}
    >
      <HoverCardPrimitive.Content
        data-slot="hover-card-content"
        align={align}
        sideOffset={sideOffset}
        css={popoverStyle}
        forceMount
        {...props}
      />
    </HoverCardPrimitive.Portal>
  );
}

const arrowStyle: Interpolation = {
  zIndex: zIndices[1],
};

function HoverCardArrow(
  props: ComponentProps<typeof HoverCardPrimitive.HoverCardArrow>,
) {
  const theme = useThemeValues();
  return (
    <HoverCardPrimitive.HoverCardArrow
      stroke="none"
      fill="none"
      width={30}
      height={15}
      css={arrowStyle}
      asChild
      {...props}
    >
      <svg>
        <path
          d="M15 10L25 0L5 0Z"
          stroke={theme.semanticColors.neutralsVeryLow}
          fill={theme.semanticColors.background}
          strokeWidth="2"
        />
        <line
          x1="8"
          x2="22"
          y1="0"
          y2="0"
          strokeWidth="3"
          strokeLinecap="round"
          stroke={theme.semanticColors.background}
        />
      </svg>
    </HoverCardPrimitive.HoverCardArrow>
  );
}

export { HoverCard, HoverCardTrigger, HoverCardContent, HoverCardArrow };
